import {
	Checkbox, Icon, IconSize, IconWeight, Input, type SelectOption, Popover, Select
} from "@clintonelec/react-storybook";
import { produce } from "immer";
import type { EmailFtpSettingsState, ScheduleUpdater } from "Interfaces";
import { type ChangeEvent, type Dispatch, memo, type SetStateAction } from "react";
import { Link } from "react-router-dom";
import "Components/ConfigLayout/Event/EmailFtpSettings/FtpServerSettings/FtpServerSettings.less";

const frequencyOptions: SelectOption[] = [ 0, 5, 10, 15, 30, 60, 120, 180, 300 ].map(value => ({
	label: value === 0 ? "Immediately" : `${ value } Sec`,
	value
}));

type FtpServerSettingsProps = {
	localEmailFtpState: [ EmailFtpSettingsState, Dispatch<SetStateAction<EmailFtpSettingsState>> ];
};

function FtpServerSettings(props: FtpServerSettingsProps) {
	const { localEmailFtpState: [ localEmailFtpSettings, setLocalEmailFtpSettings ] } = props;
	const link = (
		<div>
			Go to <Link to="/setup/system/schedule">Schedule Preset</Link> to create a new preset.
		</div>
	);

	const {
		receiver, host, port, basePath, idPassRequired, id, password, filePrefix, frequency, log
	} = localEmailFtpSettings.ftpServerSettings;

	const handleFtpReceiverUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.ftpServerSettings.receiver = value;
		}));
	};

	const handleHostUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.ftpServerSettings.host = value;
		}));
	};

	const handlePortUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.ftpServerSettings.port = +value;
		}));
	};

	const handleBasePathUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.ftpServerSettings.basePath = value;
		}));
	};

	const handleIdPassRequiredChange = (event: ChangeEvent<HTMLInputElement>) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.ftpServerSettings.idPassRequired = event.currentTarget.checked;
		}));
	};

	const handleIdUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.ftpServerSettings.id = value;
		}));
	};

	const handlePasswordUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.ftpServerSettings.password = value;
		}));
	};

	const handleFtpFilePrefixUpdate = (value: string) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.ftpServerSettings.filePrefix = value;
		}));
	};

	const handleFtpLogChange = (event: ChangeEvent<HTMLInputElement>) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.ftpServerSettings.log = event.currentTarget.checked;
		}));
	};

	const handleFtpFrequencySelect = (duration: string | null) => {
		if (!duration) {
			return;
		}

		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.ftpServerSettings.frequency = +duration;
		}));
	};

	const updateFtpScheduleChecked = (scheduleName: string) => (event: ChangeEvent<HTMLInputElement>) => {
		setLocalEmailFtpSettings(produce(localEmailFtpSettings, (draft) => {
			draft.ftpServerSettings.schedule[ scheduleName ] = event.currentTarget.checked;
		}));
	};

	const renderSchedule = (handleScheduleChange: ScheduleUpdater) => {
		return function Schedule(schedule: [ string, boolean ]) {
			const [ scheduleName, checked ] = schedule;

			return (
				<Checkbox checked={ checked } key={ scheduleName } onChange={ handleScheduleChange(scheduleName) }>
					<span>{ scheduleName }</span>
				</Checkbox>
			);
		};
	};

	const ftpSchedule = Object
		.entries(localEmailFtpSettings.ftpServerSettings.schedule)
		.map(renderSchedule(updateFtpScheduleChecked));

	return (
		<div className="ftp-server-settings-container">
			<div className="ftp-server-settings">
				<div className="column">
					<div className="header">
						<h4>FTP</h4>
					</div>
					<div className="form-row">
						<span>Receiver</span>
						<Input
							value={ receiver }
							onUpdate={ handleFtpReceiverUpdate }
							wrapClassName="form-input"
						/>
					</div>
					<div className="form-row">
						<span>Host / IP</span>
						<Input
							value={ host }
							onUpdate={ handleHostUpdate }
							wrapClassName="form-input"
						/>
					</div>
					<div className="form-row">
						<span>Port</span>
						<Input
							hideCharMessage={ true }
							max={ 65535 }
							min={ 1 }
							onUpdate={ handlePortUpdate }
							type="number"
							value={ port }
							wrapClassName="form-input"
						/>
					</div>
					<div className="form-row">
						<span>Base Path</span>
						<Input
							value={ basePath }
							onUpdate={ handleBasePathUpdate }
							wrapClassName="form-input"
						/>
					</div>
					<div className="form-row">
						<span>ID/Password Required</span>
						<Checkbox
							checked={ idPassRequired }
							className="form-input"
							onChange={ handleIdPassRequiredChange }
						/>
					</div>
					<div className="form-row">
						<span>ID</span>
						<Input
							value={ id }
							onUpdate={ handleIdUpdate }
							disabled={ !idPassRequired }
							wrapClassName="form-input"
						/>
					</div>
					<div className="form-row">
						<span>Password</span>
						<Input
							value={ password }
							onUpdate={ handlePasswordUpdate }
							disabled={ !idPassRequired }
							password
							placeholder="****"
							wrapClassName="form-input"
						/>
					</div>
					<div className="form-row">
						<span>File Prefix</span>
						<Input
							value={ filePrefix }
							onUpdate={ handleFtpFilePrefixUpdate }
							wrapClassName="form-input"
						/>
					</div>
					<div className="form-row">
						<span>Min. Frequency</span>
						<Select
							allowClear={ false }
							className="form-input"
							onSelect={ handleFtpFrequencySelect }
							options={ frequencyOptions }
							value={ frequency }
						/>
					</div>
					<div className="form-row">
						<span>Log</span>
						<Checkbox
							checked={ log }
							className="form-input"
							onChange={ handleFtpLogChange }
						/>
					</div>
				</div>
				<div className="column">
					<div className="schedule-title header">
						<h4>Schedule</h4>
						<Popover content={ link }>
							<Icon name="circle-info" iconWeight={ IconWeight.REGULAR } size={ IconSize.SMALLER } />
						</Popover>
					</div>
					<div className="schedules">
						{ ftpSchedule }
					</div>
				</div>
			</div>
		</div>
	);
}

export default memo(FtpServerSettings);
